/**
 * catalog-service.ts
 *
 * Channel-aware catalog repository with sector-specific schema extensions,
 * attachment references, canonical payload hashing, and revision tracking.
 */

import { generateUUIDv7 } from '../../crypto/uuidv7.js';
import { CommerceAuditService } from './commerce-audit-service.js';
import { globalSchemaRegistry } from './schema-registry.js';
import type {
  AttachmentRef,
  CommerceScope,
  LocalizedContent,
  Product,
  ProductOption,
  ProductStatus,
  ProductVariant,
  SchemaExtension,
} from './commerce-contracts.js';

export interface CreateProductInput {
  readonly title: LocalizedContent;
  readonly description?: LocalizedContent;
  readonly status?: ProductStatus;
  readonly categoryIds?: readonly string[];
  readonly optionDefinitions?: readonly ProductOption[];
  readonly media?: readonly AttachmentRef[];
  readonly schemaExtension?: SchemaExtension;
  readonly salesChannelIds?: readonly string[];
}

export interface UpdateProductInput {
  readonly productId: string;
  readonly expectedRevision: number;
  readonly patch: Partial<{
    readonly title: LocalizedContent;
    readonly description: LocalizedContent;
    readonly status: ProductStatus;
    readonly categoryIds: readonly string[];
    readonly optionDefinitions: readonly ProductOption[];
    readonly media: readonly AttachmentRef[];
    readonly schemaExtension: SchemaExtension;
    readonly salesChannelIds: readonly string[];
  }>;
}

export interface CreateVariantInput {
  readonly productId: string;
  readonly sku: string;
  readonly barcode?: string;
  readonly attributes: Record<string, string>;
  readonly inventoryItemId: string;
  readonly basePriceMinor: string;
  readonly currency?: string;
  readonly status?: 'active' | 'inactive';
}

export interface UpdateVariantInput {
  readonly variantId: string;
  readonly expectedRevision: number;
  readonly patch: Partial<{
    readonly basePriceMinor: string;
    readonly currency: string;
    readonly barcode: string;
    readonly attributes: Record<string, string>;
    readonly status: 'active' | 'inactive';
  }>;
}

export interface SaveListingCommand {
  readonly productId: string;
  readonly expectedProductRevision: number;
  readonly productPatch: Partial<{
    readonly title: LocalizedContent;
    readonly description: LocalizedContent;
    readonly status: ProductStatus;
    readonly categoryIds: readonly string[];
    readonly media: readonly AttachmentRef[];
    readonly schemaExtension: SchemaExtension;
    readonly salesChannelIds: readonly string[];
  }>;
  readonly variantPatches?: readonly {
    readonly variantId: string;
    readonly expectedRevision?: number;
    readonly patch: Partial<{
      readonly basePriceMinor: string;
      readonly currency: string;
      readonly barcode: string;
      readonly attributes: Record<string, string>;
      readonly status: 'active' | 'inactive';
    }>;
  }[];
  readonly idempotencyKey?: string;
}

import { SECURITY_TACTICAL_CATALOG } from './security-tactical-catalog.js';

export class CatalogService {
  private readonly products = new Map<string, Product>();
  private readonly variants = new Map<string, ProductVariant>();

  constructor() {
    this.seedDefaultCatalog();
  }

  private seedDefaultCatalog(): void {
    const defaultProducts = SECURITY_TACTICAL_CATALOG.map((item) => ({
      productId: item.productId,
      title: { 'en-US': item.name },
      description: { 'en-US': item.technicalSpec },
      categoryIds: [item.category.replace(/\s+/g, '')],
      status: 'active' as ProductStatus,
      variants: [
        {
          variantId: `var-${item.productId}`,
          sku: item.sku,
          basePriceMinor: item.priceMinorUsd,
          currency: 'USD',
          attributes: { Category: item.category },
          inventoryItemId: `inv_${item.sku}`,
        },
      ],
      schemaExtension: {
        sector: 'defense_security' as const,
        schemaVersion: '1.0.0',
        msrpUsd: item.msrpUsd,
        binLocation: item.binLocation,
      },
    }));

    const nowIso = new Date().toISOString();
    for (const p of defaultProducts) {
      const variantIds = p.variants.map((v) => v.variantId);
      const product: Product = {
        productId: p.productId,
        meshId: 'mesh-alpha',
        workspaceId: 'ws-main',
        title: p.title,
        description: p.description,
        status: p.status,
        categoryIds: p.categoryIds,
        optionDefinitions: [],
        variantIds,
        media: [],
        schemaExtension: p.schemaExtension as any,
        salesChannelIds: ['channel-storefront-web', 'channel-pos-counter-1'],
        schemaVersion: '1.0.0',
        revision: 1,
        payloadHash: `hash-${p.productId}`,
        updatedAt: nowIso,
      };
      this.products.set(p.productId, product);

      for (const v of p.variants) {
        const variant: ProductVariant = {
          variantId: v.variantId,
          productId: p.productId,
          sku: v.sku,
          attributes: v.attributes,
          inventoryItemId: v.inventoryItemId,
          basePriceMinor: v.basePriceMinor,
          currency: v.currency,
          status: 'active',
          revision: 1,
          payloadHash: `hash-${v.variantId}`,
          updatedAt: nowIso,
        };
        this.variants.set(v.variantId, variant);
      }
    }
  }

  /**
   * Validates sector-specific schema extensions against the versioned SchemaExtensionRegistry.
   */
  static validateSchemaExtension(ext?: SchemaExtension): void {
    if (!ext) return;
    const res = globalSchemaRegistry.validateExtension(ext);
    if (!res.valid) {
      throw new Error(res.errors.join('; '));
    }
  }

  /**
   * Creates a new Product in draft or active state.
   */
  async createProduct(scope: CommerceScope, input: CreateProductInput): Promise<Product> {
    CatalogService.validateSchemaExtension(input.schemaExtension);

    const productId = generateUUIDv7();
    const nowIso = new Date().toISOString();

    const rawPayload = {
      productId,
      meshId: scope.meshId,
      workspaceId: scope.workspaceId,
      title: input.title,
      description: input.description,
      status: input.status ?? 'draft',
      categoryIds: input.categoryIds ?? [],
      optionDefinitions: input.optionDefinitions ?? [],
      variantIds: [],
      media: input.media ?? [],
      schemaExtension: input.schemaExtension,
      salesChannelIds: input.salesChannelIds ?? (scope.salesChannelId ? [scope.salesChannelId] : []),
      schemaVersion: '1.0.0',
      revision: 1,
    };

    const payloadHash = await CommerceAuditService.computeSha256(
      CommerceAuditService.canonicalize(rawPayload)
    );

    const product: Product = {
      ...rawPayload,
      payloadHash,
      updatedAt: nowIso,
    };

    this.products.set(productId, product);
    return product;
  }

  /**
   * Updates an existing product with OCC revision checking.
   */
  async updateProduct(scope: CommerceScope, input: UpdateProductInput): Promise<Product> {
    const existing = this.products.get(input.productId);
    if (!existing) {
      throw new Error(`Product not found: ${input.productId}`);
    }

    if (existing.workspaceId !== scope.workspaceId || existing.meshId !== scope.meshId) {
      throw new Error(`Access denied: cross-workspace product modification prohibited`);
    }

    if (existing.revision !== input.expectedRevision) {
      throw new Error(`Revision conflict: expected revision ${input.expectedRevision}, found ${existing.revision}`);
    }

    if (input.patch.schemaExtension) {
      CatalogService.validateSchemaExtension(input.patch.schemaExtension);
    }

    const nextRevision = existing.revision + 1;
    const nowIso = new Date().toISOString();

    const updatedRaw = {
      ...existing,
      ...input.patch,
      title: input.patch.title ?? existing.title,
      status: input.patch.status ?? existing.status,
      media: input.patch.media ?? existing.media,
      schemaExtension: input.patch.schemaExtension ?? existing.schemaExtension,
      salesChannelIds: input.patch.salesChannelIds ?? existing.salesChannelIds,
      revision: nextRevision,
    };

    const payloadHash = await CommerceAuditService.computeSha256(
      CommerceAuditService.canonicalize(updatedRaw)
    );

    const updatedProduct: Product = {
      ...updatedRaw,
      payloadHash,
      updatedAt: nowIso,
    };

    this.products.set(input.productId, updatedProduct);
    return updatedProduct;
  }

  /**
   * Creates a product variant and binds it to parent product.
   */
  async createVariant(scope: CommerceScope, input: CreateVariantInput): Promise<ProductVariant> {
    const product = this.products.get(input.productId);
    if (!product) {
      throw new Error(`Cannot create variant: parent product not found: ${input.productId}`);
    }

    if (product.workspaceId !== scope.workspaceId || product.meshId !== scope.meshId) {
      throw new Error(`Access denied: cross-workspace variant creation prohibited`);
    }

    const variantId = generateUUIDv7();
    const nowIso = new Date().toISOString();

    const rawPayload = {
      variantId,
      productId: input.productId,
      sku: input.sku,
      barcode: input.barcode,
      attributes: input.attributes,
      inventoryItemId: input.inventoryItemId,
      basePriceMinor: input.basePriceMinor,
      currency: input.currency ?? 'USD',
      status: input.status ?? 'active',
      revision: 1,
    };

    const payloadHash = await CommerceAuditService.computeSha256(
      CommerceAuditService.canonicalize(rawPayload)
    );

    const variant: ProductVariant = {
      ...rawPayload,
      payloadHash,
      updatedAt: nowIso,
    };

    this.variants.set(variantId, variant);

    // Update parent product variant list
    const updatedVariants = [...product.variantIds, variantId];
    const nextProdRev = product.revision + 1;
    const updatedProdRaw = {
      ...product,
      variantIds: updatedVariants,
      revision: nextProdRev,
    };
    const prodHash = await CommerceAuditService.computeSha256(
      CommerceAuditService.canonicalize(updatedProdRaw)
    );
    this.products.set(product.productId, {
      ...updatedProdRaw,
      payloadHash: prodHash,
      updatedAt: nowIso,
    });

    return variant;
  }

  /**
   * Updates an existing variant with OCC revision checking.
   */
  async updateVariant(scope: CommerceScope, input: UpdateVariantInput): Promise<ProductVariant> {
    const existing = this.variants.get(input.variantId);
    if (!existing) {
      throw new Error(`Variant not found: ${input.variantId}`);
    }

    const parentProduct = this.products.get(existing.productId);
    if (!parentProduct || parentProduct.workspaceId !== scope.workspaceId || parentProduct.meshId !== scope.meshId) {
      throw new Error(`Access denied: cross-workspace variant modification prohibited`);
    }

    if (existing.revision !== input.expectedRevision) {
      throw new Error(`Revision conflict on variant ${existing.sku}: expected revision ${input.expectedRevision}, found ${existing.revision}`);
    }

    const nextRevision = existing.revision + 1;
    const nowIso = new Date().toISOString();

    const updatedRaw = {
      ...existing,
      ...input.patch,
      basePriceMinor: input.patch.basePriceMinor ?? existing.basePriceMinor,
      currency: input.patch.currency ?? existing.currency,
      status: input.patch.status ?? existing.status,
      barcode: input.patch.barcode ?? existing.barcode,
      attributes: input.patch.attributes ?? existing.attributes,
      revision: nextRevision,
    };

    const payloadHash = await CommerceAuditService.computeSha256(
      CommerceAuditService.canonicalize(updatedRaw)
    );

    const updatedVariant: ProductVariant = {
      ...updatedRaw,
      payloadHash,
      updatedAt: nowIso,
    };

    this.variants.set(input.variantId, updatedVariant);
    return updatedVariant;
  }

  /**
   * Transactional atomic save command updating product, media, and variant prices together.
   */
  async saveListing(scope: CommerceScope, command: SaveListingCommand): Promise<{ product: Product; variants: readonly ProductVariant[] }> {
    const updatedProduct = await this.updateProduct(scope, {
      productId: command.productId,
      expectedRevision: command.expectedProductRevision,
      patch: command.productPatch,
    });

    const updatedVariants: ProductVariant[] = [];
    if (command.variantPatches && command.variantPatches.length > 0) {
      for (const vp of command.variantPatches) {
        const currentVar = this.variants.get(vp.variantId);
        if (currentVar) {
          const expRev = vp.expectedRevision ?? currentVar.revision;
          const uVar = await this.updateVariant(scope, {
            variantId: vp.variantId,
            expectedRevision: expRev,
            patch: vp.patch,
          });
          updatedVariants.push(uVar);
        }
      }
    } else {
      updatedVariants.push(...this.getVariantsForProduct(command.productId));
    }

    return { product: updatedProduct, variants: updatedVariants };
  }

  getProduct(scope: CommerceScope, productId: string): Product | undefined {
    const p = this.products.get(productId);
    if (!p) return undefined;
    if (p.workspaceId !== scope.workspaceId || p.meshId !== scope.meshId) {
      return undefined; // OWASP BOLA scope enforcement
    }
    return p;
  }

  getVariant(variantId: string): ProductVariant | undefined {
    return this.variants.get(variantId);
  }

  getVariantsForProduct(productId: string): readonly ProductVariant[] {
    const list: ProductVariant[] = [];
    for (const v of this.variants.values()) {
      if (v.productId === productId) {
        list.push(v);
      }
    }
    return list;
  }

  listProducts(scope: CommerceScope, filter?: { categoryId?: string; channelId?: string; status?: ProductStatus }): readonly Product[] {
    const list: Product[] = [];
    for (const p of this.products.values()) {
      if (p.workspaceId !== scope.workspaceId || p.meshId !== scope.meshId) {
        continue;
      }
      if (filter?.status && p.status !== filter.status) continue;
      if (filter?.categoryId && !p.categoryIds.includes(filter.categoryId)) continue;
      if (filter?.channelId && !p.salesChannelIds.includes(filter.channelId)) continue;
      list.push(p);
    }
    return list;
  }

  getAllProducts(): readonly Product[] {
    return Array.from(this.products.values());
  }

  getAllVariants(): readonly ProductVariant[] {
    return Array.from(this.variants.values());
  }
}

export const globalCatalogService = new CatalogService();

